import { useEffect, useState } from 'react';
import { setToken } from 'slice/authSlice';
import { useDispatch } from 'react-redux';
import { hasAuthParams, useAuth as useOIDCAuth } from 'react-oidc-context';
import { useAuth } from 'hook/useAuth';

/**
 * A custom React hook that integrates with the OIDC (OpenID Connect) authentication flow.
 * It manages sign-in redirects and token initialization using the react-oidc-context library.
 * This hook automatically attempts to sign in the user if they are not already authenticated.
 * Once authenticated, it stores the user's ID token in the application's state using Redux.
 *
 * @returns {object} An object containing the token initialization status (`tokenInitialized`)
 * and any authentication error (`authError`).
 */
function useOIDC() {
  const authOIDC = useOIDCAuth();
  const { logout } = useAuth();
  const dispatch = useDispatch();
  const [tokenInitialized, setTokenInitialized] = useState(false);

  useEffect(() => {
    if (authOIDC.user?.id_token) {
      dispatch(setToken(authOIDC.user.id_token));
      setTokenInitialized(true);
    } else {
      dispatch(setToken(null));
      setTokenInitialized(false);
    }
  }, [authOIDC.user, dispatch]);

  useEffect(() => {
    const unsubscribe = authOIDC.events.addSilentRenewError(() => {
      logout(true);
    });

    return () => {
      unsubscribe(); // auto-clean
    };
  }, [authOIDC.events, logout]);

  useEffect(() => {
    const shouldRedirectToSignIn =
      !hasAuthParams() &&
      !authOIDC.isAuthenticated &&
      !authOIDC.activeNavigator &&
      !authOIDC.isLoading;

    if (shouldRedirectToSignIn) {
      authOIDC.signinRedirect();
    }

    if (authOIDC.isAuthenticated && !tokenInitialized) {
      dispatch(setToken(authOIDC.user.id_token));
      setTokenInitialized(true);
    }
  }, [authOIDC, tokenInitialized, dispatch]);

  return { tokenInitialized, oidcAuthError: authOIDC.error };
}

export default useOIDC;
